import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { take } from 'rxjs/operators';

import { IMultiSelectOption, IMultiSelectSettings, IMultiSelectTexts } from 'ngx-bootstrap-multiselect';
import { ApiError, ConfigService, MessageService } from '../../../../core';
import { ConfigEmpresa, isIdentificacaoRepresentanteNome } from '../../../configuracoes/app/empresa';
import { RepresentanteFilter } from '../filter';
import { RepresentanteNomePipe } from '../pipes';
import { RepresentanteSearchOrderBy } from '../representante-search-orderby';
import { RepresentanteSearchHttpService } from '../services';
import { representanteAtivo } from '../situacao';
import { RepresentanteModalidadeComercialValue, REPRESENTANTE_MODALIDADE_COMERCIAL_REPRESENTANTE, REPRESENTANTE_MODALIDADE_COMERCIAL_VENDA_DIRETA } from '../domain/representante-modalidade-comercial.enum';


/** Interface utilizada para selecionar as opções de modalidades comerciais */
interface ModalidadeComercialChecker {
    value: RepresentanteModalidadeComercialValue,
    isChecked: boolean;
}

@Component({
    selector: 'sgt-representante-multiselect',
    templateUrl: './representante-multiselect.component.html',
    styleUrls: ['./representante-multiselect.component.less'],
    providers: [
        RepresentanteNomePipe
    ]
})
export class RepresentanteMultiselectComponent implements OnInit, OnChanges {
    configEmpresa: ConfigEmpresa;

    /** Representantes selecionados */
    @Input() idsRepresentantes: number[];
    /** Ao alterar a seleção dos representantes */
    @Output() idsRepresentantesChange = new EventEmitter<number[]>();

    /** Se deve exibir a opção de selecionar a opção de sem representante ou seja, para filtrar registros que não possuem um representante */
    @Input() showSemRepresentante = false;
    /** Valor da opção de selecionar sem representante */
    @Input() semRepresentante = false;
    /** Ao alterar a seleção da opção sem representante */
    @Output() semRepresentanteChange = new EventEmitter<boolean>();

    /** Se deve exibir somente os representantes ativos */
    @Input() somenteAtivos = true;
    @Output() somenteAtivosChange = new EventEmitter<boolean>();

    /** Se deve exibir o filtro das modalidades comerciais */
    @Input() showModalidadesComerciais = false;
    /** Modalidades Comerciais selecionadas */
    @Input() modalidadesComerciais: number[] = [];
    @Output() modalidadesComerciaisChange = new EventEmitter<number[]>();
    /** Array com a opções de seleção das modalidades comerciais */
    modalidadesComerciaisCheckers: ModalidadeComercialChecker[] = [
        // Representante
        {
            value: REPRESENTANTE_MODALIDADE_COMERCIAL_REPRESENTANTE,
            isChecked: false,
        },
        // Venda Direta
        {
            value: REPRESENTANTE_MODALIDADE_COMERCIAL_VENDA_DIRETA,
            isChecked: false
        }
    ];

    // Settings
    settings: IMultiSelectSettings;
    @Input() pullRight = false;
    @Input() enableSearch = true;
    @Input() showCheckAll = true;
    @Input() showUncheckAll = true;
    @Input() dynamicTitleMaxItems = 0;
    @Input() fixedTitle = false;
    /** Se deve exibir o multiselect acima */
    @Input() dropup = false;
    /** Se deve desabilitar o multiselect */
    @Input() disabled = false;

    // Texts
    texts: IMultiSelectTexts;
    @Input() checkAll = 'Selecionar Todos';
    @Input() uncheckAll = 'Limpar';
    @Input() defaultTitle = 'Nenhum Representante';
    @Input() checked = 'Representante';
    @Input() checkedPlural = 'Representantes';
    @Input() searchPlaceholder = 'Pesquisar...';
    @Input() allSelected = 'Todos Selecionados';
    @Input() searchEmptyResult = 'Nenhum representante encontrado';
    @Input() searchNoRenderText = 'Digite para pesquisar';

    // Options
    options: IMultiSelectOption[];

    constructor(private configService: ConfigService,
                private messageService: MessageService,
                private representanteNomePipe: RepresentanteNomePipe,
                private representanteSearchHttpService: RepresentanteSearchHttpService) { }

    ngOnInit() {
        this.configEmpresa = this.configService.getEmpresa();
        // Settings
        this.settings = {
            pullRight: this.pullRight,
            enableSearch: this.enableSearch,
            containerClasses: `container-multiselect container-multiselect-representante ${this.dropup ? 'dropup' : 'dropdown'}`,
            showCheckAll: this.showCheckAll,
            showUncheckAll: this.showUncheckAll,
            dynamicTitleMaxItems: this.dynamicTitleMaxItems,
            fixedTitle: this.fixedTitle
        };
        // Texts
        this.texts = {
            checkAll: this.checkAll,
            uncheckAll: this.uncheckAll,
            defaultTitle: this.defaultTitle,
            checked: this.checked,
            checkedPlural: this.checkedPlural,
            searchPlaceholder: this.searchPlaceholder,
            allSelected: this.allSelected,
            searchEmptyResult: this.searchEmptyResult,
            searchNoRenderText: this.searchNoRenderText
        };

        // Modalidades Comerciais
        this.selectModalidadeComercialChecker();
        this.search();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.modalidadesComerciais && !changes.modalidadesComerciais.firstChange) {
            this.selectModalidadeComercialChecker();
        }
    }

    toggleSomenteAtivos(value: boolean) {
        this.somenteAtivos = value;
        this.somenteAtivosChange.emit(this.somenteAtivos);
        this.search();
    }

    /** Seleciona as opções das modalidades comerciais */
    private selectModalidadeComercialChecker() {
        if (!this.showModalidadesComerciais) {
            return;
        }

        // Setando se a modalidade está chegada ou não conforme array de modalidades informada via input
        this.modalidadesComerciaisCheckers.forEach(modalidadeChecker => {
            modalidadeChecker.isChecked = this.modalidadesComerciais?.includes(modalidadeChecker.value.id) ?? false;
        });
    }

    /** Ao alterar a seleção das modalidades comerciais */
    onChangeModalidadeComercialChecker() {
        this.modalidadesComerciais = [];

        // Percorrendo todas as opções e adicionando no array de modalidades apenas as selecionadas
        this.modalidadesComerciaisCheckers.forEach(modalidadeChecker => {
            if (modalidadeChecker.isChecked) {
                this.modalidadesComerciais.push(modalidadeChecker.value.id);
            }
        });

        this.modalidadesComerciaisChange.emit(this.modalidadesComerciais);
    }

    /** Cria os tipos de bonificações para o multiselect */
    private search() {
        this.options = [];
        const selects: IMultiSelectOption[] = [];

        const filter = new RepresentanteFilter();
        // Somente Ativos
        if (this.somenteAtivos) {
            filter.situacoes = [representanteAtivo.id];
        }

        let orderBy: RepresentanteSearchOrderBy;
        if (isIdentificacaoRepresentanteNome(this.configEmpresa.identificacaoRepresentante)) {
            orderBy = RepresentanteSearchOrderBy.Nome;
        } else {
            orderBy = RepresentanteSearchOrderBy.NomeFantasia;
        }

        this.representanteSearchHttpService.search(filter, orderBy, 'asc', -1, -1)
            .pipe(
                take(1)
            )
            .subscribe(
                response => {
                    if (response?.representantes) {
                        // Adicionando a opção de "Sem Representante"
                        if (this.showSemRepresentante) {
                            selects.push({
                                id: 0,
                                name: 'SEM REPRESENTANTE'
                            });

                            selects.push({
                                id: null,
                                name: null,
                                isLabel: true
                            });
                        }

                        response.representantes.forEach(representante => {
                            selects.push({
                                id: representante.id,
                                name: this.representanteNomePipe.transform(representante.nome, representante.nomeFantasia)
                            });
                        });
                        this.options = selects;
                    }
                },
                (error: ApiError) => {
                    this.messageService.error(error.message, 'Erro ao pesquisar os representantes', error);
                }
            );
    }

    /** Ao alterar a seleção da opção de sem representante */
    private onChangeSemRepresentante() {
        this.semRepresentante = this.idsRepresentantes.includes(0);
        this.semRepresentanteChange.emit(this.semRepresentante);

    }

    /** Ao alterar a seleção dos representantes */
    onChange() {
        this.onChangeSemRepresentante();
        this.idsRepresentantesChange.emit(this.idsRepresentantes);
    }
}
